import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { useAuth } from '@/hooks/useAuth';
import { LogOut, Settings, Edit, Camera, ChevronRight } from 'lucide-react';
import LoginPrompt from '@/components/shared/LoginPrompt';
import OceanBackground from '@/components/background/OceanBackground';

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = async () => {
    if (!window.confirm('确定要退出登录吗？')) return;
    setLoggingOut(true);
    try {
      await logout();
      navigate('/'); // 退出后回到首页
    } catch {
      alert('退出失败');
    } finally {
      setLoggingOut(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(180deg, #162544 0%, #1e3860 40%, #162544 100%)' }}>
        <div className="w-10 h-10 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'rgba(106,179,214,0.3)', borderTopColor: 'var(--bloom)' }} />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen relative" style={{ background: 'linear-gradient(180deg, #162544 0%, #1e3860 40%, #162544 100%)' }}>
        <Navbar />
        <OceanBackground />
        <div className="pt-28 pb-20 relative z-10">
          <LoginPrompt title="账号设置" description="登录后管理你的账号信息、安全设置和偏好" />
        </div>
      </div>
    );
  }

  const Section = ({ title, children }) => (
    <div className="frost p-6 mb-5" style={{ background: 'rgba(30,56,96,0.4)' }}>
      <h3 className="text-base font-semibold cb mb-4 flex items-center gap-2">{title}</h3>
      {children}
    </div>
  );

  const Row = ({ icon, label, desc, action, danger }) => (
    <button onClick={action} className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-[rgba(106,179,214,0.06)] transition text-left">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(61,130,176,0.1)' }}>{icon}</div>
        <div>
          <div className={`text-sm font-medium ${danger ? 'text-red-400' : 'cb'}`}>{label}</div>
          <div className="text-[11px] cd">{desc}</div>
        </div>
      </div>
      <ChevronRight className="w-4 h-4 cd" />
    </button>
  );

  return (
    <div className="min-h-screen relative" style={{ background: 'linear-gradient(180deg, #162544 0%, #1e3860 40%, #162544 100%)' }}>
      <Navbar />
      <OceanBackground />
      <div className="max-w-2xl mx-auto px-4 md:px-6 pt-24 pb-12 relative z-10">
        <h1 className="text-3xl font-bold cb mb-2">账号设置</h1>
        <p className="cd text-sm mb-8">管理你的账号信息、安全设置和偏好</p>

        <Section title={<><Edit className="w-4 h-4 cg" /> 个人信息</>}>
          <Row icon={<Camera className="w-4 h-4" />} label="头像" desc="更换头像图片" action={() => {}} />
          <Row icon={<Edit className="w-4 h-4" />} label="昵称" desc={user?.user_metadata?.full_name || '未设置'} action={() => {}} />
        </Section>

        <Section title={<><Settings className="w-4 h-4 ca" /> 安全设置</>}>
          <Row icon={<Edit className="w-4 h-4" />} label="修改密码" desc="定期更换密码更安全" action={() => {}} />
        </Section>

        <button onClick={handleLogout} disabled={loggingOut} className="w-full frost p-4 flex items-center justify-center gap-2 text-red-400 hover:!bg-[rgba(224,122,122,0.1)] transition font-medium" style={{ animation: 'rise .75s ease-out 0.4s both' }}>
          <LogOut className="w-4 h-4" /> {loggingOut ? '退出中...' : '退出登录'}
        </button>
      </div>
    </div>
  );
};

export default SettingsPage;
